import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Shield, Database, Link as LinkIcon, Lock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const FEATURE_ICONS = [Database, Shield, LinkIcon, Lock];

const CAPABILITY_IMAGES = [
  { image: '/assets/ss/smart%20search.png', reverse: false },
  { image: '/assets/ss/search%20result%20transparent%20attribution%20traceable.png', reverse: true },
  { image: '/assets/ss/knowledge%20graph.png', reverse: false },
  { image: '/assets/ss/knowledge%20hub.png', reverse: true },
  { image: '/assets/ss/tools%20integration%20connector.png', reverse: false },
];

const ProductPage = () => {
  const { t } = useLanguage();
  const lang = t('productPage');
  const features = lang.features.map((f, idx) => ({ ...f, icon: FEATURE_ICONS[idx] }));
  const capabilities = lang.capabilities.map((c, idx) => ({ ...c, ...CAPABILITY_IMAGES[idx] }));

  return (
    <div className="bg-zinc-950 grid-background min-h-screen text-zinc-100 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-block border border-zinc-800 bg-zinc-900/50 rounded-full px-4 py-1.5 text-sm mb-8"
        >
          <span className="text-zinc-300">{lang.badge}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-4xl md:text-6xl font-bold tracking-tight mb-6"
        >
          {lang.headline} <span className="gradient-text">{lang.headlineHighlight}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-lg md:text-xl text-zinc-400 max-w-3xl mx-auto mb-12"
        >
          {lang.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="relative max-w-[70.4rem] mx-auto rounded-2xl border border-zinc-800 bg-zinc-900 aspect-[17.2/9] overflow-hidden shadow-2xl"
        >
          <video
            src="https://storage.googleapis.com/testing-api-neuronlink/recordings/0414.mp4"
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          {features.map((f, idx) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="flex gap-4 p-6 rounded-xl border border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/60 transition-colors"
            >
              <div className="flex-shrink-0 mt-1">
                <f.icon className="w-6 h-6 text-zinc-300" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-zinc-100 mb-2">{f.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Platform Capabilities Section */}
      <div className="my-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight">{lang.capabilitiesHeadline} <span className="gradient-text">{lang.capabilitiesHighlight}</span></h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">{lang.capabilitiesSubheadline}</p>
        </div>

        <div className="flex flex-col gap-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {capabilities.map((cap, idx) => (
            <motion.div
              key={cap.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className={`flex flex-col md:flex-row items-center gap-12 ${cap.reverse ? 'md:flex-row-reverse' : ''}`}
            >
              <div className="flex-1 space-y-6">
                <h3 className="text-2xl md:text-4xl font-bold tracking-tight text-white mb-2">
                  {cap.title}
                </h3>
                <p className="text-lg text-zinc-400 leading-relaxed max-w-xl">
                  {cap.description}
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative rounded-2xl border border-zinc-800 bg-zinc-900 shadow-2xl overflow-hidden aspect-[16/10]">
                    <img
                      src={cap.image}
                      alt={cap.title}
                      className="w-full h-full object-cover select-none"
                      draggable="false"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-10 sm:p-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-zinc-100">
            {lang.ctaHeadline}
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            {lang.ctaSubheadline}
          </p>
          <a
            href="/#demo"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-white text-zinc-950 font-semibold text-sm hover:bg-zinc-100 transition-colors"
          >
            {lang.ctaButton} <CheckCircle2 className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductPage;
